import React, { useMemo, useState } from "react";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { useDialogStyles } from "../../theme/redesign";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  RecordCard,
  RecordCardMeta,
  RecordsCardsGrid,
  useViewMode,
  ViewModeToggle,
} from "../../components/ViewModeToggle";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { addDaysIso, formatDate, matchesQuery, moneyBoth, moneyPair, todayIso } from "./helpers";
import { DialogClose, EmptyHint, FilterChips, InfoDialog, LoadingHint, NegocioPager, PageFrame, RelatedLinks, SearchField, StatusChip, stopRow, tNegocio, usePager } from "./shared";
import { ProductMark } from "./AccountPeek";
import { DualMoneyField } from "./AccountFields";
import { productOfferLabel } from "./catalog";
import { useStyles } from "./styles";

const isPermanentFlag = (v) => v === true || v === 1 || v === "1";
const MIN = 2;
const MAX = 6;

const emptyCombo = () => ({
  name: "",
  description: "",
  saleType: "profile",
  price: "",
  priceFx: "",
  priceCurrency: "local",
  isPermanent: true,
  durationDays: 30,
  productIds: [],
  isActive: true,
  isNew: true,
});

export default function CombosPage() {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [viewMode, setViewMode] = useViewMode("negocio-combos", "cards");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [dialog, setDialog] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [info, setInfo] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/negocio/combos");
      setRecords(data.records || []);
    } catch (e) {
      toastError(e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    reload();
  }, []);

  const loadEligible = async (saleType) => {
    try {
      const { data } = await api.get("/negocio/combos/eligible", { params: { saleType } });
      setEligible(data.records || []);
    } catch (e) {
      toastError(e);
      setEligible([]);
    }
  };

  const rows = useMemo(
    () =>
      records.filter((c) => {
        const okStatus =
          !status ||
          (status === "active" && c.isActive !== false) ||
          (status === "disabled" && c.isActive === false);
        return okStatus && matchesQuery(c, query, [c.name, c.description]);
      }),
    [records, query, status]
  );
  const pager = usePager(rows);

  const openCreate = () => {
    const next = emptyCombo();
    setDialog(next);
    loadEligible(next.saleType);
  };

  const openEdit = (c) => {
    const pair = moneyPair(c.price, c.priceFx);
    setDialog({
      ...emptyCombo(),
      ...c,
      price: pair.local,
      priceFx: pair.fx,
      priceCurrency: c.priceCurrency === "fx" ? "fx" : "local",
      productIds: (c.items || []).map((i) => i.productId || i.product?.id),
      isNew: false,
    });
    loadEligible(c.saleType || "profile");
  };

  const patchDialog = (patch) => setDialog((d) => ({ ...d, ...patch }));

  const onType = (saleType) => {
    patchDialog({ saleType, productIds: [] });
    loadEligible(saleType);
  };

  const toggleProduct = (id) => {
    const pid = Number(id);
    const current = (dialog?.productIds || []).map(Number);
    if (current.includes(pid)) {
      patchDialog({ productIds: current.filter((x) => x !== pid) });
      return;
    }
    if (current.length >= MAX) {
      toast.error(tNegocio("pages.combos.maxProducts"));
      return;
    }
    patchDialog({ productIds: [...current, pid] });
  };

  const saveCombo = async () => {
    if (!dialog) return;
    if (!String(dialog.name || "").trim()) {
      toast.error(tNegocio("fields.name"));
      return;
    }
    if ((dialog.productIds || []).length < MIN || (dialog.productIds || []).length > MAX) {
      toast.error(tNegocio("pages.combos.rangeHint"));
      return;
    }
    if (!isPermanentFlag(dialog.isPermanent) && !Number(dialog.durationDays)) {
      toast.error(tNegocio("fields.durationDays"));
      return;
    }
    try {
      const payload = {
        name: dialog.name,
        description: dialog.description,
        saleType: dialog.saleType,
        price: moneyPair(dialog.price, dialog.priceFx).local,
        priceFx: moneyPair(dialog.price, dialog.priceFx).fx,
        priceCurrency: dialog.priceCurrency === "fx" ? "fx" : "local",
        isPermanent: isPermanentFlag(dialog.isPermanent),
        durationDays: dialog.isPermanent ? null : +dialog.durationDays,
        productIds: dialog.productIds,
        isActive: dialog.isActive !== false,
      };
      if (dialog.isNew) await api.post("/negocio/combos", payload);
      else await api.put(`/negocio/combos/${dialog.id}`, payload);
      toast.success(tNegocio("toastSaved"));
      setDialog(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    try {
      await api.delete(`/negocio/combos/${confirm.id}`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(null);
      reload();
    } catch (e) {
      toastError(e);
    }
  };

  const renderActions = (c) => (
    <div className={classes.actionCluster}>
      <Tooltip title={tNegocio("edit")}>
        <IconButton size="small" onClick={stopRow(() => openEdit(c))}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={tNegocio("pages.products.remove")}>
        <IconButton size="small" onClick={stopRow(() => setConfirm(c))}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const durationLabel = (c) => {
    if (c.isPermanent === false || c.isPermanent === 0 || c.isPermanent === "0") {
      return `${tNegocio("pages.combos.until")} ${formatDate(c.endsAt)}`;
    }
    return tNegocio("pages.combos.permanent");
  };

  const selectedCount = (dialog?.productIds || []).length;
  const endsPreview =
    dialog && !isPermanentFlag(dialog.isPermanent)
      ? addDaysIso(todayIso(), Number(dialog.durationDays || 0))
      : "";

  return (
    <>
      <PageFrame
        title={tNegocio("pages.combos.title")}
        subtitle={tNegocio("pages.combos.subtitle")}
        actions={
          <>
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            <Button variant="contained" color="primary" onClick={openCreate}>
              {tNegocio("new.combo")}
            </Button>
          </>
        }
      >
        <div className={classes.toolbar}>
          <FilterChips
            value={status}
            onChange={setStatus}
            options={[
              { value: "", label: tNegocio("filters.all") },
              { value: "active", label: tNegocio("status.active") },
              { value: "disabled", label: tNegocio("status.disabled") },
            ]}
          />
          <SearchField value={query} onChange={setQuery} />
        </div>

        {rows.length && viewMode === "cards" ? (
          <>
          <RecordsCardsGrid>
            {pager.slice.map((c) => (
              <RecordCard key={c.id} actions={renderActions(c)} onClick={() => setInfo(c)}>
                <div className={classes.productHead}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 800 }}>{c.name}</div>
                    <RecordCardMeta>{c.description || tNegocio("pages.combos.noDescription")}</RecordCardMeta>
                  </div>
                  <StatusChip value={c.isActive !== false ? "active" : "disabled"} />
                </div>
                <div className={classes.profileDots} style={{ marginTop: 10 }}>
                  {(c.items || []).map((item) => (
                    <Tooltip key={item.id || item.productId} title={item.product?.name || ""}>
                      <span><ProductMark product={item.product} /></span>
                    </Tooltip>
                  ))}
                </div>
                <div className={classes.productStats}>
                  <div>
                    <div className={classes.captionBlock}>{tNegocio("cols.price")}</div>
                    <strong>{moneyBoth(c.price, c.priceFx)}</strong>
                  </div>
                  <div>
                    <div className={classes.captionBlock}>{tNegocio("fields.saleType")}</div>
                    <strong>
                      {tNegocio(
                        c.saleType === "full"
                          ? "pages.accounts.saleTypes.full"
                          : "pages.accounts.saleTypes.profile"
                      )}
                    </strong>
                  </div>
                  <div>
                    <div className={classes.captionBlock}>{tNegocio("pages.combos.validity")}</div>
                    <strong>{durationLabel(c)}</strong>
                  </div>
                </div>
              </RecordCard>
            ))}
          </RecordsCardsGrid>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
          </>
        ) : rows.length ? (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tNegocio("cols.name")}</TableCell>
                  <TableCell>{tNegocio("cols.product")}</TableCell>
                  <TableCell>{tNegocio("fields.saleType")}</TableCell>
                  <TableCell>{tNegocio("cols.price")}</TableCell>
                  <TableCell>{tNegocio("pages.combos.validity")}</TableCell>
                  <TableCell>{tNegocio("cols.status")}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.slice.map((c) => (
                  <TableRow key={c.id} hover className={classes.clickableRow} onClick={() => setInfo(c)}>
                    <TableCell>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      {c.description ? <div className={classes.captionBlock}>{c.description}</div> : null}
                    </TableCell>
                    <TableCell>
                      <div className={classes.profileDots}>
                        {(c.items || []).map((item) => (
                          <Tooltip key={item.id || item.productId} title={item.product?.name || ""}>
                            <span><ProductMark product={item.product} /></span>
                          </Tooltip>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {tNegocio(
                        c.saleType === "full"
                          ? "pages.accounts.saleTypes.full"
                          : "pages.accounts.saleTypes.profile"
                      )}
                    </TableCell>
                    <TableCell>{moneyBoth(c.price, c.priceFx)}</TableCell>
                    <TableCell>{durationLabel(c)}</TableCell>
                    <TableCell>
                      <StatusChip value={c.isActive !== false ? "active" : "disabled"} />
                    </TableCell>
                    <TableCell align="right" className={classes.stickyActions} onClick={(e) => e.stopPropagation()}>
                      {renderActions(c)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <NegocioPager
              count={pager.count}
              page={pager.page}
              rowsPerPage={pager.rowsPerPage}
              onChangePage={pager.setPage}
              onChangeRowsPerPage={pager.setRowsPerPage}
            />
          </div>
        ) : loading ? (
          <LoadingHint />
        ) : (
          <EmptyHint
            title={tNegocio("pages.combos.emptyTitle")}
            subtitle={tNegocio("pages.combos.empty")}
          />
        )}
      </PageFrame>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => setDialog(null)} />
        <DialogTitle>{dialog?.isNew ? tNegocio("new.combo") : tNegocio("edit")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.sectionTitle}>{tNegocio("fields.saleType")}</div>
          <div className={classes.typePick}>
            <div
              className={`${classes.typeCard} ${dialog?.saleType === "profile" ? classes.typeCardOn : ""}`}
              onClick={() => onType("profile")}
              role="button"
              tabIndex={0}
            >
              <strong>{tNegocio("pages.accounts.saleTypes.profile")}</strong>
              <div className={classes.captionBlock}>{tNegocio("pages.combos.typeProfileHint")}</div>
            </div>
            <div
              className={`${classes.typeCard} ${dialog?.saleType === "full" ? classes.typeCardOn : ""}`}
              onClick={() => onType("full")}
              role="button"
              tabIndex={0}
            >
              <strong>{tNegocio("pages.accounts.saleTypes.full")}</strong>
              <div className={classes.captionBlock}>{tNegocio("pages.combos.typeFullHint")}</div>
            </div>
          </div>
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={dialog?.name || ""}
            onChange={(e) => patchDialog({ name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.description")}
            value={dialog?.description || ""}
            onChange={(e) => patchDialog({ description: e.target.value })}
          />
          <DualMoneyField
            label={tNegocio("fields.comboPrice")}
            hint={tNegocio("pages.combos.priceHint")}
            localValue={dialog?.price}
            fxValue={dialog?.priceFx}
            currency={dialog?.priceCurrency || "local"}
            onChange={({ local, fx, currency }) =>
              patchDialog({ price: local, priceFx: fx, priceCurrency: currency })
            }
          />
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={isPermanentFlag(dialog?.isPermanent)}
                onChange={(e) => patchDialog({ isPermanent: e.target.checked })}
              />
            }
            label={tNegocio("fields.isPermanent")}
          />
          {isPermanentFlag(dialog?.isPermanent) === false ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              label={tNegocio("fields.durationDays")}
              value={dialog?.durationDays ?? 30}
              onChange={(e) => patchDialog({ durationDays: +e.target.value })}
              helperText={`${tNegocio("pages.combos.until")} ${endsPreview || "—"}`}
            />
          ) : (
            <div className={classes.captionBlock}>{tNegocio("pages.combos.permanentHint")}</div>
          )}
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={dialog?.isActive !== false}
                onChange={(e) => patchDialog({ isActive: e.target.checked })}
              />
            }
            label={tNegocio("fields.isActive")}
          />
          <div className={classes.sectionTitle} style={{ marginTop: 16 }}>
            {tNegocio("pages.combos.included")} ({selectedCount}/{MAX})
          </div>
          <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
            {tNegocio("pages.combos.rangeHint")}
          </div>
          {(eligible || []).length ? (
            eligible.map((p) => {
              const checked = (dialog?.productIds || []).map(Number).includes(Number(p.id));
              return (
                <div key={p.id} className={classes.productHead} style={{ padding: "6px 0" }}>
                  <Checkbox color="primary" checked={checked} onChange={() => toggleProduct(p.id)} />
                  <ProductMark product={p} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{productOfferLabel(p, tNegocio)}</div>
                    <div className={classes.captionBlock}>
                      {p.viableAccounts} {tNegocio("pages.combos.viableAccounts")}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={classes.captionBlock}>{tNegocio("pages.combos.noEligible")}</div>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" variant="contained" onClick={saveCombo}>
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>

      <InfoDialog
        open={!!info}
        onClose={() => setInfo(null)}
        title={info?.name}
        subtitle={info?.description}
        chips={<StatusChip value={info?.isActive !== false ? "active" : "disabled"} />}
        fields={[
          { label: tNegocio("cols.price"), value: moneyBoth(info?.price) },
          {
            label: tNegocio("fields.saleType"),
            value: info
              ? tNegocio(
                  info.saleType === "full"
                    ? "pages.accounts.saleTypes.full"
                    : "pages.accounts.saleTypes.profile"
                )
              : "",
          },
          { label: tNegocio("pages.combos.validity"), value: info ? durationLabel(info) : "" },
        ]}
        extra={
          <>
            <div className={classes.profileDots} style={{ marginTop: 8 }}>
              {(info?.items || []).map((item) => (
                <Tooltip key={item.id || item.productId} title={item.product?.name || ""}>
                  <span><ProductMark product={item.product} /></span>
                </Tooltip>
              ))}
            </div>
            <RelatedLinks items={[{ to: "/negocio/productos", label: tNegocio("related.products") }]} />
          </>
        }
        actions={
          <Button color="primary" variant="contained" onClick={() => { const c = info; setInfo(null); openEdit(c); }}>
            {tNegocio("edit")}
          </Button>
        }
      />
      <ConfirmationModal title={tNegocio("confirmTitle")} open={!!confirm} onClose={() => setConfirm(null)} onConfirm={runConfirm}>
        {tNegocio("confirmBody")}
      </ConfirmationModal>
    </>
  );
}
